import { useQuery } from '@tanstack/react-query'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable'
import { IconMiniButton } from 'components/FbUI/FbButton'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { ContactResponse } from 'models/contacts'
import { useEffect, useMemo } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { BsDownload } from 'react-icons/bs'
import useApiService from 'services/api'
import { useDownload } from 'features/download-widget'
import { getDownloadStatus, startActivityExportV2 } from 'services/downloadsApi'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'
import { IColumnVisibility } from '../../DataTable/types'
import { TableSearch } from '../../Filters/TableSearch'
import { FilterChips } from '../../Filters/components/FilterChips/FilterChips'
import * as S from '../CommonTable.styles'
import ActivityV2Filterset from './ActivityV2Filterset'
import { useActivityTableColumnsV2 } from './useActivityTableColumnsV2'

type SortableFields = (keyof ContactResponse | string)[]

const sortableFields: SortableFields = [
  'user',
  'content_type',
  'created_at',
  'bulk_size',
]

const PAGE_SIZE = 100

interface ActivityTableV2Props {
  setTotalRowsCount?: (count: number) => void
  myActivityOnly?: boolean
  tableKey: string
}

function ActivityTableV2Component(props: ActivityTableV2Props) {
  const {
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()
  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()

  const api = useApiService()

  const activityV2FilterStore = filterStoreRepo.getStore('ACTIVITY_TABLE_V2')
  const { page, setPage } = activityV2FilterStore()
  const filterParams = useFilterParams(activityV2FilterStore)

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
    }),
    [filterParams, sortParams]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
  })

  // DATA FETCHING
  const { isFetching, data } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      clearSelectedRows()

      return await api.getActivityListV2(
        {
          ...filterAndSortParams,
          page: page,
          my_activity: props.myActivityOnly,
        },
        signal
      )
    },
  })
  // END DATA FETCHING

  // Filter out document events with empty field_history_objects
  // These are internal versioning events (is_deleted, deleted_at, etc)
  const filteredData = useMemo(() => {
    if (!data?.results) return []

    return data.results.filter((event) => {
      if (event.content_type?.model === 'document') {
        // For document events, require field_history_objects to have data
        return (
          event.field_history_objects && event.field_history_objects.length > 0
        )
      }
      return true
    })
  }, [data?.results])

  const columns = useActivityTableColumnsV2()

  const { startDownload } = useDownload()

  const downloadCsv = async () => {
    const { sort, ...filterWithoutSort } = filterAndSortParams as Record<
      string,
      unknown
    >

    await startDownload({
      items: [{ id: 0, name: 'Exporting Activity...' }],
      initiate: async () => {
        const response = await startActivityExportV2({
          selected_columns: [],
          export_type: 'csv',
          filter_params: {
            ...filterWithoutSort,
            // Only include my_activity when true — sending `false` stringifies to
            // "False" in the backend export task's QueryDict, which is truthy in
            // Python and silently overrides the user filter. Match the GET list
            // call's behavior (which omits the key when undefined). See FIR-6809.
            ...(props.myActivityOnly ? { my_activity: true } : {}),
          },
          sort: sort as string | undefined,
        })
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getDownloadStatus,
      label: 'Activity',
    })
  }

  useEffect(() => {
    const totalCount = data?.count ?? 0
    props.setTotalRowsCount?.(totalCount)
    setTotalRowsInBackend?.(totalCount)
  }, [data?.count])

  const tooltip = (
    <Tooltip id="tooltip">Download limited to 10,000 rows</Tooltip>
  )

  return (
    <DataTableContainer>
      <DataTable
        tableHeader={
          <S.SearchContainer>
            <S.SearchContainerFilters>
              <S.TableSearchContainer>
                <TableSearch
                  filterStore={activityV2FilterStore}
                  searchPlaceholder="Search by company or email address"
                  tableKey={props.tableKey}
                />
              </S.TableSearchContainer>
              <S.TableButtonsContainer>
                <ActivityV2Filterset />
                <ColumnSelectorRecipient tableKey={props.tableKey} />
              </S.TableButtonsContainer>
            </S.SearchContainerFilters>
            <FilterChips
              identifier={'ACTIVITY_TABLE_V2'}
              store={activityV2FilterStore}
              clearAllButton
              showActive
            />
          </S.SearchContainer>
        }
        key={props.tableKey}
        loading={isFetching}
        tableKey={props.tableKey}
        data={filteredData}
        columns={columns}
        sortableFields={sortableFields}
        virtualizeRows={true}
        isPaginationEnabled={true}
        stickyLastColumn
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        footerControls={
          <OverlayTrigger placement="top" overlay={tooltip}>
            <div>
              <IconMiniButton onClick={() => downloadCsv()} loading={false}>
                <BsDownload size={18} />
              </IconMiniButton>
            </div>
          </OverlayTrigger>
        }
      />
    </DataTableContainer>
  )
}

export function ActivityTableV2(
  props: ActivityTableV2Props & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  return (
    <ColumnsStoreProvider
      tableKey={props.tableKey}
      savedPresetsIdentifier={'ACTIVITY_TABLE_V2'}
      defaultColumnVisibility={defaultColumnVisibility}
    >
      <DataTableProvider tableKey={tableKey}>
        <ActivityTableV2Component tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
